/**
 * Deferred deep link — operator UI (feat #177).
 *
 * Composition:
 *   - Gradient emerald->sky header that explains the flow.
 *   - Stats card — tenant-wide counts and the live resolution rate.
 *   - Mint-link card — operators can stage a deferred link (target URL,
 *     optional deep link path, TTL, platform, campaign, context JSON).
 *   - Links table — status pill, target URL, platform, expiry, claimed
 *     count, and inline toggle / extend / delete actions.
 *
 * Brand: emerald->sky headline, zinc borders, 6-8px rounded, light only.
 * No emojis, no competitor names.
 */
import {
	Fragment,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createDeferredLink,
	deferredDeepLinkQueryKeys,
	deleteLink,
	formatDuration,
	formatPercent,
	formatUnixSec,
	listClaims,
	listLinks,
	parseContext,
	PLATFORMS,
	statsWindow,
	statusOf,
	timeUntilExpiry,
	updateLink,
	validateFingerprint,
	validateTargetUrl,
	validateDeepLinkPath,
	validateId,
	validateTtlSeconds,
	type DeferredDeepLink,
	type DeferredDeepLinkClaim,
	type DeferredPlatform,
	type DeferredStats,
	type LinkStatus,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const SELECT = INPUT;
const TEXTAREA = `${INPUT} font-mono text-xs min-h-[96px]`;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";

const STATUS_PILL: Record<LinkStatus, string> = {
	live: "rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700",
	claimed:
		"rounded-[6px] border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700",
	expired:
		"rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600",
	disabled:
		"rounded-[6px] border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface DeferredDeepLinkPageProps {
	initialFingerprint?: string;
	initialCampaignId?: string;
}

export function DeferredDeepLinkPage(
	props: DeferredDeepLinkPageProps = {},
) {
	const queryClient = useQueryClient();

	const [fingerprintFilter, setFingerprintFilter] = useState<string>(
		props.initialFingerprint ?? "",
	);
	const [campaignFilter, setCampaignFilter] = useState<string>(
		props.initialCampaignId ?? "",
	);

	const appliedFp = useMemo(
		() => validateFingerprint(fingerprintFilter) ?? undefined,
		[fingerprintFilter],
	);
	const appliedCampaign = useMemo(
		() => validateId(campaignFilter) ?? undefined,
		[campaignFilter],
	);

	const linksQuery = useQuery({
		queryKey: deferredDeepLinkQueryKeys.links({
			fingerprint_hash: appliedFp,
			campaign_id: appliedCampaign,
		}),
		queryFn: () =>
			listLinks({
				fingerprint_hash: appliedFp,
				campaign_id: appliedCampaign,
				limit: 100,
			}),
	});

	const statsQuery = useQuery({
		queryKey: deferredDeepLinkQueryKeys.stats(),
		queryFn: () => statsWindow(),
	});

	const createMutation = useMutation({
		mutationFn: createDeferredLink,
		onSuccess: (link) => {
			toast.success(
				`Link for ${new URL(link.target_url).host} staged.`,
			);
			queryClient.invalidateQueries({
				queryKey: deferredDeepLinkQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to stage link.");
		},
	});

	const updateMutation = useMutation({
		mutationFn: updateLink,
		onSuccess: () => {
			toast.success("Link updated.");
			queryClient.invalidateQueries({
				queryKey: deferredDeepLinkQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to update link.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteLink,
		onSuccess: () => {
			toast.success("Link removed.");
			queryClient.invalidateQueries({
				queryKey: deferredDeepLinkQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to delete link.");
		},
	});

	const [expandedLinkId, setExpandedLinkId] = useState<string | null>(null);

	const rows = linksQuery.data ?? [];

	return (
		<main
			data-testid="deferred-deep-link-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<StatsCard
				stats={statsQuery.data ?? null}
				loading={statsQuery.isLoading}
				error={statsQuery.error?.message ?? null}
			/>

			<MintLinkCard
				onCreate={(args) => createMutation.mutate(args)}
				busy={createMutation.isPending}
			/>

			<LinksCard
				rows={rows}
				loading={linksQuery.isLoading}
				error={linksQuery.error?.message ?? null}
				fingerprintFilter={fingerprintFilter}
				campaignFilter={campaignFilter}
				onFingerprintChange={setFingerprintFilter}
				onCampaignChange={setCampaignFilter}
				expandedLinkId={expandedLinkId}
				onToggleExpand={(id) =>
					setExpandedLinkId((prev) => (prev === id ? null : id))
				}
				onToggleEnabled={(link) =>
					updateMutation.mutate({
						id: link.id,
						enabled: link.enabled !== 1,
					})
				}
				onExtend={(link) =>
					updateMutation.mutate({
						id: link.id,
						extend_ttl_seconds: 60 * 60 * 24 * 7,
					})
				}
				onDelete={(link) => {
					if (typeof window !== "undefined") {
						const ok = window.confirm(
							`Remove deferred link to ${link.target_url}?`,
						);
						if (!ok) return;
					}
					deleteMutation.mutate(link.id);
				}}
				busy={updateMutation.isPending || deleteMutation.isPending}
			/>
		</main>
	);
}

export default DeferredDeepLinkPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Deferred deep links
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Resolve new-install landing intent. A visitor taps a web link, the
				app store opens, and when your app launches for the first time, this
				service hands it the original target URL, deep link path, and
				context payload. Replays of the same install never double-claim.
			</p>
		</header>
	);
}

function StatsCard(props: {
	stats: DeferredStats | null;
	loading: boolean;
	error: string | null;
}) {
	const { stats, loading, error } = props;

	return (
		<section
			className={`${CARD} p-5`}
			aria-label="Deferred deep link stats"
			data-testid="ddl-stats-card"
		>
			<div className="flex items-end justify-between gap-2">
				<h2 className="text-base font-semibold text-zinc-900">
					30-day snapshot
				</h2>
				{stats ? (
					<span className={HINT}>
						Window {formatUnixSec(stats.window_start)} — {" "}
						{formatUnixSec(stats.window_end)}
					</span>
				) : null}
			</div>

			{loading ? (
				<p className="mt-4 text-sm text-zinc-500">Loading.</p>
			) : error ? (
				<p className="mt-4 text-sm text-rose-600">{error}</p>
			) : stats ? (
				<Fragment>
					<div
						className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5"
						data-testid="ddl-stats-totals"
					>
						<StatCell
							label="Links"
							value={stats.totals.links.toLocaleString()}
						/>
						<StatCell
							label="Live"
							value={stats.totals.live.toLocaleString()}
						/>
						<StatCell
							label="Claimed"
							value={stats.totals.claims.toLocaleString()}
						/>
						<StatCell
							label="Unique installs"
							value={stats.totals.unique_installs.toLocaleString()}
						/>
						<StatCell
							label="Resolution rate"
							value={formatPercent(stats.totals.resolution_rate)}
						/>
					</div>

					{stats.per_platform.length > 0 ? (
						<div className="mt-5">
							<p className={LABEL}>Platforms</p>
							<ul className="mt-2 flex flex-wrap gap-2">
								{stats.per_platform.map((row) => (
									<li
										key={row.platform}
										className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-700"
									>
										<span className="font-medium text-zinc-900">
											{row.platform}
										</span>
										<span className="mx-1 text-zinc-400">/</span>
										<span>{row.links} links</span>
										<span className="mx-1 text-zinc-400">/</span>
										<span>{row.claims} claims</span>
									</li>
								))}
							</ul>
						</div>
					) : null}
				</Fragment>
			) : (
				<p className="mt-4 text-sm text-zinc-500">No data yet.</p>
			)}
		</section>
	);
}

function StatCell(props: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-1">
			<span className={HINT}>{props.label}</span>
			<span className="text-2xl font-semibold tracking-tight text-zinc-900">
				{props.value}
			</span>
		</div>
	);
}

function MintLinkCard(props: {
	onCreate: (args: {
		target_url: string;
		deep_link_path?: string | null;
		campaign_id?: string | null;
		platform?: DeferredPlatform;
		ttl_seconds?: number;
		context?: Record<string, unknown> | null;
	}) => void;
	busy: boolean;
}) {
	const [targetUrl, setTargetUrl] = useState("");
	const [deepLinkPath, setDeepLinkPath] = useState("");
	const [campaignId, setCampaignId] = useState("");
	const [platform, setPlatform] = useState<DeferredPlatform>("ios");
	const [ttl, setTtl] = useState<string>(String(60 * 60 * 24 * 7));
	const [contextJson, setContextJson] = useState<string>("{}");
	const [errors, setErrors] = useState<{
		target_url?: string;
		deep_link_path?: string;
		campaign_id?: string;
		ttl?: string;
		context?: string;
	}>({});

	const disabled = props.busy;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const next: typeof errors = {};

		const targetOk = validateTargetUrl(targetUrl);
		if (!targetOk)
			next.target_url = "Enter an http(s) URL under 2048 characters.";

		let deepOk: string | null = null;
		if (deepLinkPath.trim().length > 0) {
			deepOk = validateDeepLinkPath(deepLinkPath);
			if (!deepOk)
				next.deep_link_path = "Path must be printable, under 1024 characters.";
		}

		let campaignOk: string | null = null;
		if (campaignId.trim().length > 0) {
			campaignOk = validateId(campaignId);
			if (!campaignOk)
				next.campaign_id = "Campaign id must match A-Za-z0-9 . _ : - (<=128).";
		}

		const ttlOk = validateTtlSeconds(ttl);
		if (ttlOk === null)
			next.ttl = "TTL must be 60 seconds to 90 days.";

		let contextParsed: Record<string, unknown> | null = null;
		const rawContext = contextJson.trim();
		if (rawContext.length > 0) {
			try {
				const parsed = JSON.parse(rawContext) as unknown;
				if (
					parsed === null ||
					typeof parsed !== "object" ||
					Array.isArray(parsed)
				) {
					next.context = "Context must be a JSON object.";
				} else {
					contextParsed = parsed as Record<string, unknown>;
				}
			} catch {
				next.context = "Context must be valid JSON.";
			}
		}

		setErrors(next);
		if (Object.keys(next).length > 0) return;

		props.onCreate({
			target_url: targetOk!,
			deep_link_path: deepOk,
			campaign_id: campaignOk,
			platform,
			ttl_seconds: ttlOk ?? undefined,
			context: contextParsed,
		});

		// Clear the target so operators can batch-mint.
		setTargetUrl("");
	};

	return (
		<section className={`${CARD} p-5`} aria-label="Stage deferred deep link">
			<h2 className="text-base font-semibold text-zinc-900">
				Stage a link
			</h2>
			<p className={HINT}>
				Publish a deferred deep link so first-launch installs can resolve
				back to the original landing target.
			</p>

			<form
				onSubmit={handleSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
				data-testid="mint-ddl-form"
			>
				<div className="flex flex-col gap-1.5 sm:col-span-2">
					<label htmlFor="target-url" className={LABEL}>
						Target URL
					</label>
					<input
						id="target-url"
						name="target_url"
						type="url"
						required
						maxLength={2048}
						className={INPUT}
						value={targetUrl}
						onChange={(e) => setTargetUrl(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-ddl-target"
					/>
					{errors.target_url ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.target_url}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5 sm:col-span-2">
					<label htmlFor="deep-link-path" className={LABEL}>
						Deep link path (optional)
					</label>
					<input
						id="deep-link-path"
						name="deep_link_path"
						type="text"
						maxLength={1024}
						className={INPUT}
						value={deepLinkPath}
						placeholder="myapp://offer/123"
						onChange={(e) => setDeepLinkPath(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-ddl-path"
					/>
					{errors.deep_link_path ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.deep_link_path}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="ddl-campaign" className={LABEL}>
						Campaign id (optional)
					</label>
					<input
						id="ddl-campaign"
						name="campaign_id"
						type="text"
						maxLength={128}
						className={INPUT}
						value={campaignId}
						onChange={(e) => setCampaignId(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-ddl-campaign"
					/>
					{errors.campaign_id ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.campaign_id}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="ddl-platform" className={LABEL}>
						Platform
					</label>
					<select
						id="ddl-platform"
						name="platform"
						className={SELECT}
						value={platform}
						onChange={(e) =>
							setPlatform(e.currentTarget.value as DeferredPlatform)
						}
						disabled={disabled}
						data-testid="mint-ddl-platform"
					>
						{PLATFORMS.map((p) => (
							<option key={p} value={p}>
								{p}
							</option>
						))}
					</select>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="ddl-ttl" className={LABEL}>
						TTL (seconds)
					</label>
					<input
						id="ddl-ttl"
						name="ttl_seconds"
						type="number"
						min={60}
						max={90 * 24 * 60 * 60}
						className={INPUT}
						value={ttl}
						onChange={(e) => setTtl(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-ddl-ttl"
					/>
					{errors.ttl ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.ttl}
						</p>
					) : (
						<p className={HINT}>Default 7 days (604800 seconds).</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5 sm:col-span-2">
					<label htmlFor="ddl-context" className={LABEL}>
						Context (JSON object, optional)
					</label>
					<textarea
						id="ddl-context"
						name="context"
						className={TEXTAREA}
						value={contextJson}
						onChange={(e) => setContextJson(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-ddl-context"
					/>
					{errors.context ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.context}
						</p>
					) : (
						<p className={HINT}>
							Delivered verbatim to the app on first-launch claim.
						</p>
					)}
				</div>

				<div className="sm:col-span-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={disabled}
						data-testid="mint-ddl-submit"
					>
						{props.busy ? "Staging." : "Stage link"}
					</button>
				</div>
			</form>
		</section>
	);
}

function LinksCard(props: {
	rows: readonly DeferredDeepLink[];
	loading: boolean;
	error: string | null;
	fingerprintFilter: string;
	campaignFilter: string;
	onFingerprintChange: (v: string) => void;
	onCampaignChange: (v: string) => void;
	expandedLinkId: string | null;
	onToggleExpand: (id: string) => void;
	onToggleEnabled: (link: DeferredDeepLink) => void;
	onExtend: (link: DeferredDeepLink) => void;
	onDelete: (link: DeferredDeepLink) => void;
	busy: boolean;
}) {
	return (
		<section
			className={`${CARD} flex flex-col`}
			aria-label="Deferred deep links"
		>
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1">
					<h2 className="text-base font-semibold text-zinc-900">
						Links ({props.rows.length})
					</h2>
					<p className={HINT}>
						Most recent first. Expired and disabled links stay visible
						until you remove them.
					</p>
				</div>
				<div className="flex flex-col gap-2 sm:flex-row">
					<input
						type="text"
						placeholder="Filter by fingerprint hash"
						aria-label="Filter by fingerprint hash"
						maxLength={256}
						className={`${INPUT} sm:w-64`}
						value={props.fingerprintFilter}
						onChange={(e) => props.onFingerprintChange(e.currentTarget.value)}
						disabled={props.busy}
						data-testid="ddl-filter-fingerprint"
					/>
					<input
						type="text"
						placeholder="Filter by campaign id"
						aria-label="Filter by campaign id"
						maxLength={128}
						className={`${INPUT} sm:w-56`}
						value={props.campaignFilter}
						onChange={(e) => props.onCampaignChange(e.currentTarget.value)}
						disabled={props.busy}
						data-testid="ddl-filter-campaign"
					/>
				</div>
			</header>

			{props.loading ? (
				<p className="p-5 text-sm text-zinc-500">Loading links.</p>
			) : props.error ? (
				<p className="p-5 text-sm text-rose-600">{props.error}</p>
			) : props.rows.length === 0 ? (
				<p className="p-5 text-sm text-zinc-500">
					No links staged yet. Start by staging one above.
				</p>
			) : (
				<table className="w-full text-sm" data-testid="ddl-links-table">
					<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
						<tr>
							<th className="px-5 py-2 font-medium">Target</th>
							<th className="px-5 py-2 font-medium">Platform</th>
							<th className="px-5 py-2 font-medium">Status</th>
							<th className="px-5 py-2 font-medium">Expires in</th>
							<th className="px-5 py-2 font-medium">Claims</th>
							<th className="px-5 py-2 font-medium"></th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-100">
						{props.rows.map((link) => (
							<LinkRow
								key={link.id}
								link={link}
								expanded={props.expandedLinkId === link.id}
								onToggleExpand={() => props.onToggleExpand(link.id)}
								onToggleEnabled={() => props.onToggleEnabled(link)}
								onExtend={() => props.onExtend(link)}
								onDelete={() => props.onDelete(link)}
								busy={props.busy}
							/>
						))}
					</tbody>
				</table>
			)}
		</section>
	);
}

function LinkRow(props: {
	link: DeferredDeepLink;
	expanded: boolean;
	onToggleExpand: () => void;
	onToggleEnabled: () => void;
	onExtend: () => void;
	onDelete: () => void;
	busy: boolean;
}) {
	const { link } = props;
	const status = statusOf(link);
	const remaining = timeUntilExpiry(link);

	return (
		<Fragment>
			<tr className="hover:bg-zinc-50" data-testid={`ddl-link-row-${link.id}`}>
				<td className="max-w-[280px] truncate px-5 py-3 text-zinc-900">
					<span className="font-medium">{link.target_url}</span>
					{link.deep_link_path ? (
						<span className="block text-xs text-zinc-500">
							{link.deep_link_path}
						</span>
					) : null}
				</td>
				<td className="px-5 py-3 text-zinc-700">{link.platform}</td>
				<td className="px-5 py-3">
					<span className={STATUS_PILL[status]}>{status}</span>
				</td>
				<td className="px-5 py-3 text-zinc-700">
					{status === "live" ? formatDuration(remaining) : "—"}
				</td>
				<td className="px-5 py-3 text-zinc-700">{link.claimed_count}</td>
				<td className="px-5 py-3 text-right">
					<div className="inline-flex flex-wrap justify-end gap-2">
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={props.onToggleExpand}
							disabled={props.busy}
							data-testid={`ddl-row-expand-${link.id}`}
						>
							{props.expanded ? "Hide" : "Claims"}
						</button>
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={props.onToggleEnabled}
							disabled={props.busy}
							data-testid={`ddl-row-toggle-${link.id}`}
						>
							{link.enabled === 1 ? "Disable" : "Enable"}
						</button>
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={props.onExtend}
							disabled={props.busy}
							data-testid={`ddl-row-extend-${link.id}`}
						>
							+ 7d
						</button>
						<button
							type="button"
							className={BTN_DANGER}
							onClick={props.onDelete}
							disabled={props.busy}
							data-testid={`ddl-row-delete-${link.id}`}
						>
							Delete
						</button>
					</div>
				</td>
			</tr>
			{props.expanded ? (
				<tr>
					<td colSpan={6} className="bg-zinc-50 px-5 py-4">
						<ClaimsDrawer link={link} />
					</td>
				</tr>
			) : null}
		</Fragment>
	);
}

function ClaimsDrawer(props: { link: DeferredDeepLink }) {
	const claimsQuery = useQuery({
		queryKey: deferredDeepLinkQueryKeys.claims(props.link.id),
		queryFn: () => listClaims(props.link.id, 25),
	});

	const context = parseContext(props.link.context);

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<KeyVal label="Token" value={props.link.claim_token} mono />
				<KeyVal label="Fingerprint" value={props.link.fingerprint_hash} mono />
				<KeyVal
					label="Expires at"
					value={formatUnixSec(props.link.ttl_expires_at)}
				/>
			</div>

			{Object.keys(context).length > 0 ? (
				<div>
					<p className={LABEL}>Context</p>
					<pre className="mt-1 max-h-40 overflow-auto rounded-[6px] border border-zinc-200 bg-white p-3 text-xs text-zinc-700">
						{JSON.stringify(context, null, 2)}
					</pre>
				</div>
			) : null}

			<div>
				<p className={LABEL}>Claims</p>
				{claimsQuery.isLoading ? (
					<p className="mt-1 text-xs text-zinc-500">Loading.</p>
				) : claimsQuery.error ? (
					<p className="mt-1 text-xs text-rose-600">
						{claimsQuery.error.message}
					</p>
				) : (claimsQuery.data ?? []).length === 0 ? (
					<p className="mt-1 text-xs text-zinc-500">No claims yet.</p>
				) : (
					<ul
						className="mt-2 space-y-1"
						data-testid={`ddl-claims-list-${props.link.id}`}
					>
						{(claimsQuery.data ?? []).map((claim) => (
							<ClaimRow key={claim.id} claim={claim} />
						))}
					</ul>
				)}
			</div>
		</div>
	);
}

function ClaimRow(props: { claim: DeferredDeepLinkClaim }) {
	return (
		<li className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-700">
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
				<span className="font-mono text-zinc-900">
					{props.claim.install_identifier}
				</span>
				<span className="text-zinc-400">/</span>
				<span>{formatUnixSec(props.claim.claim_at)}</span>
				{props.claim.ip_hash ? (
					<Fragment>
						<span className="text-zinc-400">/</span>
						<span className="font-mono">{props.claim.ip_hash}</span>
					</Fragment>
				) : null}
			</div>
		</li>
	);
}

function KeyVal(props: { label: string; value: string; mono?: boolean }) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className={HINT}>{props.label}</span>
			<span
				className={
					props.mono
						? "truncate font-mono text-xs text-zinc-800"
						: "text-sm text-zinc-800"
				}
				title={props.value}
			>
				{props.value}
			</span>
		</div>
	);
}

// Named re-export for the root wire-up cycle.
export { DeferredDeepLinkPage as DeferredDeepLinkFeaturePage };
